<template>
  <FullScreenLayout>
    <div class="main">
      <!-- HEADER -->
      <header class="header">
        <div class="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <div class="buttons">
                <svg-icon name="huge-earth" class="w-5 h-5" />
                {{ currentLocaleTitle }}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                v-for="loc in $options.locales"
                :key="loc.id"
                :class="
                  loc.locale === currentLocale ? 'text-muted-foreground' : ''
                "
                @click="changeLocale(loc.locale)"
              >
                {{ loc.title }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <!-- CARD -->
      <div class="card" :class="{ 'card-first': currentStep === 1 }">
        <div class="content">
          <!-- Back + Logo -->
          <div
            class="flex items-center w-full self-stretch"
            :class="currentStep > 1 ? 'justify-between' : 'justify-center'"
          >
            <div v-if="currentStep > 1" class="back" @click="goBack">
              <svg-icon name="huge-chevron-left" class="w-8 h-8" />
            </div>

            <div class="flex items-center justify-center">
              <img :src="logo" alt="logo" class="logo" />
            </div>
            <div v-if="currentStep > 1" class="w-8 h-8 opacity-0" />
          </div>

          <!-- Title block -->
          <div class="title">
            <div class="flex h-[68px] flex-col items-center gap-3">
              <p class="text-sm text-muted-foreground">{{ currentStep }} / 3</p>
              <h2 class="heading">
                {{ title }}
              </h2>
            </div>
            <p class="bottom">
              {{ subtitle }}
            </p>
          </div>

          <!-- STEPS -->
          <transition name="fade" mode="out-in">
            <div :key="currentStep" class="steps">
              <!-- STEP 1 — режим -->
              <template v-if="currentStep === 1">
                <div
                  v-for="option in modes"
                  :key="option.value"
                  @click="selectMode(option.value)"
                  class="item"
                  :class="selectedClass(option.value)"
                >
                  <div class="logo-icon" :class="option.bgClass">
                    <svg-icon
                      :color="option.iconColor"
                      :name="option.name"
                      class="w-6 h-6"
                    />
                  </div>
                  <div>
                    <p class="font-bold text-foreground leading-6">
                      {{ $t(option.title) }}
                    </p>
                    <p class="text-sm text-muted-foreground">
                      {{ $t(option.subtitle) }}
                    </p>
                  </div>
                </div>
              </template>

              <!-- STEP 2 — ЭЦП -->
              <template v-if="currentStep === 2">
                <div
                  class="item"
                  :class="
                    isEcpSelected ? 'selected' : 'bg-card border border-border'
                  "
                  @click="selectECP"
                >
                  <div class="logo-icon shrink-0 bg-primary relative">
                    <Spinner v-if="ecpLoading" size="sm" class="absolute" />
                    <svg-icon
                      name="huge-key"
                      class="w-6 h-6 text-primary-foreground"
                    />
                  </div>
                  <div>
                    <p class="font-bold text-foreground">
                      {{ $t("Выбрать ЭЦП") }}
                    </p>
                    <p class="text-sm text-muted-foreground">
                      {{ $t("Для выбора ключа необходимо наличие NCALayer") }}
                    </p>
                  </div>
                </div>

                <p
                  v-if="errorMessage"
                  class="flex justify-start w-full text-sm text-destructive"
                >
                  {{ errorMessage }}
                </p>

                <p
                  v-if="ncaLayerWaiting"
                  class="flex justify-start w-full text-sm text-orange-500"
                >
                  {{ $t("NCALAYER_WAITING") }}
                </p>

                <p
                  v-if="isEcpSelected && !errorMessage && !ncaLayerWaiting"
                  class="flex justify-start w-full text-sm text-orange-500"
                >
                  {{ $t("Окно выбора ЭЦП возможно уже открыто") }}
                </p>
              </template>

              <!-- STEP 3 — выбор роли -->
              <template v-if="currentStep === 3">
                <div class="role selected">
                  <div>
                    <p class="text-sm font-bold leading-6 text-foreground">
                      {{
                        (roles.find((r) => r.code === "INDIVIDUAL") || {})
                          .name || "INDIVIDUAL"
                      }}
                    </p>
                  </div>
                </div>

                <p
                  v-if="errorMessage"
                  class="flex justify-start w-full text-sm text-destructive"
                >
                  {{ errorMessage }}
                </p>

                <!-- AGREEMENT -->
              </template>
            </div>
          </transition>

          <!-- BUTTON -->
          <div class="w-full flex flex-col">
            <a
              v-if="currentStep === 2"
              class="link-text"
              href="https://ncl.pki.gov.kz/"
              target="_blank"
            >
              {{ $t("Скачать NCALayer") }}
            </a>
            <div class="flex flex-col gap-6" v-if="currentStep === 3">
              <div class="agree">
                <div class="flex justify-start items-center gap-3">
                  <Checkbox v-model="agreement" />

                  <div class="leading-5 line-clamp-3">
                    <span
                      class="text-sm text-foreground cursor-pointer select-none"
                      @click="agreement = !agreement"
                    >
                      {{ $t("Я ознакомлен и принимаю") + " " }}
                    </span>
                    <span class="link-text text-sm">
                      {{ $t("Пользовательское соглашение") + " " }}
                    </span>
                    <span class="text-sm text-foreground">
                      {{ $t("и") + " " }}
                    </span>
                    <span class="link-text text-sm">
                      {{ $t("согласие на обработку персональных данных") }}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                variant="default"
                class="!h-14 w-full !text-base !font-medium"
                :disabled="!selectedRole || !agreement || loading"
                @click="confirmRole"
                size="lg"
                style="border-radius: var(--radius)"
              >
                <Loader2 v-if="loading" class="animate-spin mr-2 w-5 h-5" />
                {{ $t("Далее") }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </FullScreenLayout>
</template>

<script>
import { Spinner } from "@/shared/ui/spinner";
import { Loader2 } from "lucide-vue-next";
import logo from "/src/assets/styles/q-logo-green.png";
import Button from "@/shared/ui/button/Button.vue";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import Checkbox from "@/shared/ui/checkbox/Checkbox.vue";
import AuthService from "@/shared/auth/AuthService";

import { authPort } from "@/shared/auth/authPort";
import { locales } from "@/shared/config/i18n";
import { tokenStorage } from "@/shared/auth/tokenStorage";
import { ncaLayerService } from "@/shared/nclayer/ncalayer.service";

const storagePort = {
  save(k, v) {
    localStorage.setItem(k, v);
  },
  load(k) {
    return localStorage.getItem(k);
  },
};

export default {
  name: "AuthRegistrationFlow",
  locales,
  components: {
    Spinner,
    Loader2,
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Checkbox,
  },

  data() {
    return {
      loading: false,

      logo,
      isEcpSelected: false,
      ecpLoading: false,
      ncaLayerWaiting: false,

      currentStep: 1,
      agreement: false,

      selectedMode: null,
      selectedRole: null,

      sessionId: null,
      roles: [],

      errorMessage: "",

      modes: [
        {
          value: "init",
          title: "Авторизация",
          subtitle: "Войти через ЭЦП",
          name: "huge-user",
          bgClass: "bg-primary",
          iconColor: "hsl(var(--primary-foreground))",
        },
        {
          value: "registration",
          title: "Регистрация",
          subtitle: "Создать аккаунт через ЭЦП",
          name: "huge-add-user",
          bgClass: "bg-muted",
          iconColor: "hsl(var(--foreground))",
        },
      ],
    };
  },

  computed: {
    currentLocale() {
      return this.$i18n.locale;
    },
    currentLocaleTitle() {
      if (this.currentLocale) {
        const locale = this.$options.locales.find(
          (l) => l.value === this.currentLocale
        );
        return locale && locale.title;
      }
      return "";
    },
    title() {
      if (this.currentStep === 1) return this.$t("Выберите действие");
      if (this.currentStep === 2) {
        return this.selectedMode === "registration"
          ? this.$t("Регистрация")
          : this.$t("Войти");
      }
      if (this.currentStep === 3) return this.$t("Выберите роль");
    },
    subtitle() {
      if (this.currentStep === 1) return this.$t("Авторизация или регистрация");
      if (this.currentStep === 2) return this.$t("Выберите ЭЦП ключ");
      if (this.currentStep === 3) return this.$t("Роль в системе");
    },
  },
  mounted() {
    let locale = localStorage.getItem("lang");
    if (locale) {
      this.$i18n.locale = locale;
    }
  },

  beforeUnmount() {
    ncaLayerService.stopChecking();
  },

  methods: {
    goBack() {
      if (this.currentStep > 1) this.currentStep--;
    },

    selectedClass(mode) {
      return this.selectedMode === mode
        ? "selected"
        : "bg-card border border-border";
    },

    selectMode(value) {
      this.selectedMode = value;
      this.currentStep = 2;
    },

    async selectECP() {
      if (this.isEcpSelected) {
        this.errorMessage = this.$t(
          "Окно выбора ЭЦП уже открыто. Пожалуйста, закройте его или выберите ключ."
        );
        return;
      }

      this.errorMessage = "";
      this.isEcpSelected = true;
      this.ecpLoading = true;

      try {
        const available = await ncaLayerService.checkConnection();

        if (!available) {
          this.ncaLayerWaiting = true;
          ncaLayerService.openNCALayer();

          try {
            await ncaLayerService.waitForConnection();
          } catch {
            throw new Error(this.$t("NCALAYER_NOT_RUNNING"));
          } finally {
            this.ncaLayerWaiting = false;
          }
        }

        const service = new AuthService(
          authPort,
          storagePort,
          this.selectedMode
        );

        const initRes = await service.login("nclayer");

        if (!initRes._success) {
          this.isEcpSelected = false;
          throw new Error(initRes.message);
        }

        this.sessionId = initRes.session_id;
        this.roles = initRes.roles;

        const individualRole = this.roles.find((r) => r.code === "INDIVIDUAL");
        if (individualRole) {
          this.selectedRole = individualRole.code;
          this.currentStep = 3;
        } else {
          throw new Error(this.$t("У вас нет роли физ лицо"));
        }
      } catch (err) {
        this.errorMessage = err.message;
        this.isEcpSelected = false;
        this.ecpLoading = false;
      } finally {
        this.ecpLoading = false;
      }
    },

    async confirmRole() {
      this.loading = true;
      this.errorMessage = "";

      try {
        const service = new AuthService(authPort, storagePort);
        const strategy = service.getStrategy("nclayer");

        const res = await strategy.confirm(this.sessionId, this.selectedRole);

        if (res?._success && res.access_token) {
          tokenStorage.setFromConfirm(res);
          this.$message.success(this.$t("Добро пожаловать!"));
          // Re-bootstrap БЕЗ перезагрузки страницы — иначе in-memory
          // access-токен теряется и фронт дёргает /login/refresh/.
          setTimeout(() => {
            if (typeof window.__bootstrapApp === "function") {
              window.__bootstrapApp();
            } else {
              window.location.reload();
            }
          }, 300);
          return;
        }

        this.errorMessage = res?.message || this.$t("Confirm failed");
      } catch (err) {
        this.errorMessage = err.message;
      } finally {
        this.loading = false;
      }
    },
    async changeLocale(locale) {
      this.$i18n.locale = locale;
      localStorage.setItem("lang", locale);
    },
  },
};
</script>

<style lang="scss" scoped>
.main {
  @apply w-full min-h-screen flex flex-col justify-center items-center p-4 gap-[94px];
  background-color: hsl(var(--muted));

  .header {
    @apply absolute top-4 left-4 right-4 flex justify-end items-center;

    .buttons {
      @apply flex items-center gap-2 p-3 outline-none text-sm font-medium leading-6;
      color: hsl(var(--foreground));
      background-color: hsl(var(--accent));
      border-radius: var(--radius);
      cursor: pointer;
      &:hover {
        background-color: hsl(var(--accent));
        opacity: 0.8;
      }
      svg {
        color: hsl(var(--muted-foreground));
      }
    }
  }

  .card {
    @apply h-[764px] flex p-12 gap-[10px];
    background-color: hsl(var(--card));
    color: hsl(var(--card-foreground));
    border-radius: 24px;
    border: 1px solid hsl(var(--border));

    &.card-first {
      @apply h-[704px];
    }

    .content {
      @apply w-[479px] flex flex-col items-center gap-16 overflow-hidden;
      flex: 1 0 0;

      .title {
        @apply flex flex-col items-center text-center gap-4;

        .heading {
          @apply text-[32px] leading-9 font-medium;
          color: hsl(var(--foreground));
        }

        .bottom {
          @apply text-xl font-light;
          color: hsl(var(--muted-foreground));
        }
      }

      .steps {
        @apply flex flex-col items-center gap-6 self-stretch overflow-y-auto;
      }

      .item {
        @apply min-w-[120px] w-full flex items-center px-5 py-4 gap-5 cursor-pointer;
        @apply border transition self-stretch;
        border-radius: var(--radius);
        border-color: hsl(var(--border));
      }
      .role {
        @apply w-full flex items-center p-5 gap-5 cursor-pointer text-left;
        border-radius: var(--radius);
        @apply border transition select-none focus:outline-none;
        border-color: hsl(var(--border));
      }
    }

    .link-text {
      @apply text-base leading-5 font-medium text-center cursor-pointer;
      color: hsl(var(--muted-foreground));
      &:hover {
        text-decoration: underline;
        color: hsl(var(--primary));
      }
    }

    .agree {
      @apply flex justify-between items-center p-5 gap-5 mt-auto border transition;
      border-radius: var(--radius);
      border-color: hsl(var(--border));

      .link-text {
        @apply text-sm font-medium cursor-pointer;
        color: hsl(var(--muted-foreground));
        &:hover {
          text-decoration: underline;
          color: hsl(var(--primary));
        }
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.logo-icon {
  @apply flex items-center justify-center rounded-full;
  width: 50px;
  height: 50px;
}

.logo {
  @apply w-auto pl-2;
  height: 40px;
}

.back {
  @apply w-8 h-8 flex items-center justify-center p-1.5;
  background-color: hsl(var(--muted));
  border-radius: 24px;
  transition: background-color 0.2s;
  &:hover {
    background-color: hsl(var(--accent));
  }

  svg {
    color: hsl(var(--muted-foreground));
  }
}

.selected {
  border-color: hsl(var(--ring));
  background-color: hsl(var(--accent));
}

@media (max-width: 1366px) {
  .main {
    @apply pb-16 gap-6;

    .card {
      @apply h-[616px] justify-center items-start;

      &.card-first {
        @apply h-[616px];
      }

      .agree {
        @apply h-20;
      }

      .content {
        @apply gap-8 self-stretch w-[364px];

        .title {
          @apply gap-1;

          .heading {
            @apply text-2xl leading-8;
          }

          .bottom {
            @apply text-base leading-6;
          }
        }
      }
    }
  }
}
</style>
